import { randomDelay } from "@/lib/utils/sleep";

// Telas cliente-facing do dono da imobiliária (realm agency_admin). Reaproveita
// o MESMO modelo de ticket do backoffice (#116/#117), mas no recorte do cliente:
// canal sempre `portal`, requesterType sempre `org`, sem notas internas,
// atribuição, SLA ou procedimentos (isso é staff).

export type TicketStatus =
  | "open"
  | "in_progress"
  | "waiting_customer"
  | "resolved"
  | "closed";
export type TicketPriority = "low" | "medium" | "high" | "urgent";
export type TicketCategory =
  | "billing"
  | "technical"
  | "account"
  | "feature_request"
  | "other";

export interface TicketMessage {
  id: string;
  author: string;
  authorRole: "customer" | "agent";
  body: string;
  at: string;
}

export interface SupportTicket {
  id: string;
  code: string;
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
  messages: TicketMessage[];
  // Atribuição na tabela compartilhada que o backoffice resolve (read-only no portal).
  /** Sempre `organization` no recorte do portal da imobiliária. */
  requesterType: "organization";
  /** Sempre `portal`. */
  channel: "portal";
  /** Nome da imobiliária solicitante. */
  requesterName: string;
}

export interface CreateTicketInput {
  subject: string;
  description: string;
  category: TicketCategory;
  priority: TicketPriority;
}

const REQUESTER = "Imobiliária Horizonte";

const tickets: SupportTicket[] = [
  {
    id: "tk-1",
    code: "TK-512",
    subject: "Dúvida sobre a fatura de maio",
    description:
      "O valor da fatura deste mês veio diferente do mês passado. Poderiam detalhar a cobrança?",
    category: "billing",
    priority: "medium",
    status: "waiting_customer",
    createdAt: "2026-05-28T13:20:00Z",
    updatedAt: "2026-05-29T10:05:00Z",
    requesterType: "organization",
    channel: "portal",
    requesterName: REQUESTER,
    messages: [
      {
        id: "m1",
        author: REQUESTER,
        authorRole: "customer",
        body: "O valor da fatura deste mês veio diferente do mês passado. Poderiam detalhar a cobrança?",
        at: "2026-05-28T13:20:00Z",
      },
      {
        id: "m2",
        author: "Suporte ImobGo",
        authorRole: "agent",
        body: "Olá! A diferença é referente a 2 novos seats ativados em maio. Posso enviar o detalhamento por e-mail?",
        at: "2026-05-29T10:05:00Z",
      },
    ],
  },
  {
    id: "tk-2",
    code: "TK-498",
    subject: "Corretor não consegue acessar o app",
    description:
      "Um corretor da equipe relata erro de login no aplicativo após o convite.",
    category: "technical",
    priority: "high",
    status: "in_progress",
    createdAt: "2026-05-30T09:00:00Z",
    updatedAt: "2026-05-30T16:42:00Z",
    requesterType: "organization",
    channel: "portal",
    requesterName: REQUESTER,
    messages: [
      {
        id: "m1",
        author: REQUESTER,
        authorRole: "customer",
        body: "Um corretor da equipe relata erro de login no aplicativo após o convite.",
        at: "2026-05-30T09:00:00Z",
      },
      {
        id: "m2",
        author: "Suporte ImobGo",
        authorRole: "agent",
        body: "Estamos verificando o vínculo do convite. Qual o e-mail do corretor?",
        at: "2026-05-30T16:42:00Z",
      },
    ],
  },
  {
    id: "tk-3",
    code: "TK-471",
    subject: "Sugestão: relatório por equipe",
    description:
      "Seria ótimo ter um relatório de produção agrupado por equipe, não só por corretor.",
    category: "feature_request",
    priority: "low",
    status: "resolved",
    createdAt: "2026-05-20T11:10:00Z",
    updatedAt: "2026-05-22T08:30:00Z",
    requesterType: "organization",
    channel: "portal",
    requesterName: REQUESTER,
    messages: [
      {
        id: "m1",
        author: REQUESTER,
        authorRole: "customer",
        body: "Seria ótimo ter um relatório de produção agrupado por equipe, não só por corretor.",
        at: "2026-05-20T11:10:00Z",
      },
      {
        id: "m2",
        author: "Suporte ImobGo",
        authorRole: "agent",
        body: "Obrigado pela sugestão! Registramos no roadmap e avisaremos quando estiver disponível.",
        at: "2026-05-22T08:30:00Z",
      },
    ],
  },
];

let ticketSeq = 600;
let msgSeq = 0;

export async function listTickets(): Promise<SupportTicket[]> {
  await randomDelay();
  // newest-first por updatedAt
  return [...tickets].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export async function getTicket(id: string): Promise<SupportTicket | undefined> {
  await randomDelay();
  const t = tickets.find((x) => x.id === id);
  return t ? { ...t, messages: [...t.messages] } : undefined;
}

export async function createTicket(
  input: CreateTicketInput,
): Promise<SupportTicket> {
  await randomDelay(700, 1300);
  ticketSeq += 1;
  const now = new Date().toISOString();
  const ticket: SupportTicket = {
    id: `tk-${ticketSeq}`,
    code: `TK-${ticketSeq}`,
    subject: input.subject,
    description: input.description,
    category: input.category,
    priority: input.priority,
    status: "open",
    createdAt: now,
    updatedAt: now,
    requesterType: "organization",
    channel: "portal",
    requesterName: REQUESTER,
    messages: [
      {
        id: `nm-${(msgSeq += 1)}`,
        author: REQUESTER,
        authorRole: "customer",
        body: input.description,
        at: now,
      },
    ],
  };
  tickets.unshift(ticket);
  return ticket;
}

export async function replyTicket(
  id: string,
  body: string,
): Promise<SupportTicket> {
  await randomDelay(400, 900);
  const ticket = tickets.find((x) => x.id === id);
  if (!ticket) throw new Error("Ticket não encontrado.");
  const now = new Date().toISOString();
  ticket.messages.push({
    id: `nm-${(msgSeq += 1)}`,
    author: REQUESTER,
    authorRole: "customer",
    body,
    at: now,
  });
  ticket.updatedAt = now;
  // Cliente respondeu → volta a aguardar o suporte.
  if (ticket.status === "waiting_customer") ticket.status = "in_progress";
  return { ...ticket, messages: [...ticket.messages] };
}
